import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ComplianceBlockedAddress } from '@shared/api/contracts/compliance';
import type { ReasonFactorValue } from '@shared/ui';
import { addressBlockReasonKey, RiskBandBadge } from '@entities/compliance';
import { formatDateTime } from '@shared/lib/format-date';
import { Button, ReasonFactorDialog, Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TypographyMuted } from '@shared/ui';

interface BlockedAddressesPanelProps {
  items: ComplianceBlockedAddress[];
  reviewer: boolean;
  loading?: boolean;
  onUnblock: (id: number, value: ReasonFactorValue) => Promise<boolean>;
}

export function BlockedAddressesPanel({ items, reviewer, loading, onUnblock }: BlockedAddressesPanelProps) {
  const { t } = useTranslation('cabinet');
  const [id, setId] = useState<number | null>(null);

  if (!items.length) return <TypographyMuted className="block py-10 text-center">{t('compliance.addresses.empty')}</TypographyMuted>;

  return (
    <div className="surface-card min-w-0 space-y-3">
      <div className="min-w-0 overflow-x-auto">
        <Table className="min-w-[56rem]">
          <TableHeader>
            <TableRow>
              <TableHead>{t('compliance.columns.merchant')}</TableHead>
              <TableHead>{t('withdrawals.columns.address')}</TableHead>
              <TableHead>{t('compliance.columns.reason')}</TableHead>
              <TableHead>{t('compliance.columns.risk')}</TableHead>
              <TableHead>{t('withdrawals.columns.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.id}>
                <TableCell className="max-w-[12rem] truncate">{item.user.email}</TableCell>
                <TableCell className="max-w-[14rem] break-all font-mono text-xs">
                  {item.network} · {item.address}
                </TableCell>
                <TableCell>{t(addressBlockReasonKey(item.amlBlockReason))}</TableCell>
                <TableCell>
                  <RiskBandBadge band={item.lastRiskBand} score={item.lastRiskscore} />
                  <span className="mt-1 block text-xs text-muted-foreground">{formatDateTime(item.lastAmlCheckedAt ?? '')}</span>
                </TableCell>
                <TableCell>
                  <Button type="button" size="sm" variant="outline" disabled={!reviewer || loading} onClick={() => setId(item.id)}>
                    {t('compliance.addresses.unblock')}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <ReasonFactorDialog
        open={id !== null}
        title={t('compliance.addresses.unblockTitle')}
        description={t('compliance.addresses.unblockDescription')}
        confirmLabel={t('compliance.addresses.unblock')}
        loading={loading}
        onOpenChange={(open) => {
          if (!open) setId(null);
        }}
        onConfirm={(value) => {
          if (id === null) return;
          void onUnblock(id, value).then((ok) => {
            if (ok) setId(null);
          });
        }}
      />
    </div>
  );
}
